// src/pages/HistoryPage.tsx
import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase/firebase';
import AppTheme from '../theme/AppTheme';
import ColorModeSelect from '../theme/ColorModeSelect';
import ProfileDropdown from '../components/ProfileDropdown';

export interface CargoAnalysisData {
  tons: number;
  from: string;
  to: string;
  analysis: {
    mode: string;
    price: string;
    day: string;
  }[];
}

const HistoryPage: React.FC = () => {
  const [user, setUser] = React.useState<any>(null);
  const [searches, setSearches] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedAnalysis, setSelectedAnalysis] = React.useState<CargoAnalysisData | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);

  // Monitor user authentication
  React.useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return unsubscribeAuth;
  }, []);

  // Fetch user's search history from Firestore
  React.useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'cargo_searches'),
      where('email', '==', user.email)
    );
    
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const searchData: any[] = [];
        querySnapshot.forEach((doc) => {
          searchData.push({ id: doc.id, ...doc.data() });
        });
        setSearches(searchData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching cargo search history: ', error);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [user]);

  const handleViewDetails = (analysis: CargoAnalysisData) => {
    setSelectedAnalysis(analysis);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAnalysis(null);
  };

  // Component to display cargo analysis results
  const CargoAnalysisDisplay: React.FC<{ analysis: CargoAnalysisData }> = ({ analysis }) => {
   
    const validOptions = analysis.analysis.filter(option => 
      option.price && 
      option.day && 
      option.price.toLowerCase() !== 'n/a' && 
      option.day.toLowerCase() !== 'n/a'
    );

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Cargo Transport Options
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {analysis.tons} ton(s) from <strong>{analysis.from}</strong> to <strong>{analysis.to}</strong>
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Available Transport Modes: {validOptions.length} of {analysis.analysis.length}
          </Typography>
          {validOptions.length > 0 ? (
            validOptions.map((option, index) => (
              <Paper 
                key={index} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1 
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {option.mode.toUpperCase()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Cost: ${option.price}`} 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={`Delivery in ${option.day} day(s)`} 
                    color="secondary" 
                    variant="outlined" 
                  />
                </Box>
              </Paper>
            ))
          ) : (
            <Typography color="error">
              No valid transport options found for this route and cargo weight.
            </Typography>
          )}

          {/* Show unavailable options with N/A values with reduced opacity */}
          {analysis.analysis.length > validOptions.length && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
                Unavailable Options:
              </Typography>
              {analysis.analysis
                .filter(option => 
                  !option.price || 
                  !option.day || 
                  option.price.toLowerCase() === 'n/a' || 
                  option.day.toLowerCase() === 'n/a'
                )
                .map((option, index) => (
                  <Paper 
                    key={`unavailable-${index}`} 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1,
                      opacity: 0.6
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {option.mode.toUpperCase()}
                    </Typography>
                    <Typography color="error" variant="body2">
                      Not available for this cargo weight/route
                    </Typography>
                  </Paper>
                ))
              }
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      
      <Box
        sx={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 2000,
          display: 'flex',
          gap: 1,
        }}
      >
        <ColorModeSelect />
        <ProfileDropdown
          userName={user?.displayName || 'User'}
          userEmail={user?.email || 'unknown@example.com'}
        />
      </Box>

      <Container sx={{ mt: 6 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Cargo Search History
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : searches.length === 0 ? (
          <Typography align="center" variant="body1" sx={{ mt: 2 }}>
            You haven't made any cargo queries yet.
          </Typography>
        ) : (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {searches.map((search) => {
              const { id, query, analysis, timestamp } = search;
              const timeString = timestamp?.toDate
                ? timestamp.toDate().toLocaleString()
                : String(timestamp);

              return (
                <Grid item xs={12} sm={6} md={4} key={id}>
                  <Card
                    component={Paper}
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 2px 5px rgba(0,0,0,0.4)'
                          : '0 2px 5px rgba(0,0,0,0.1)',
                    }}
                  >
                    <CardHeader
                      titleTypographyProps={{ variant: 'h6' }}
                      title={`${query.tons} ton(s) cargo`}
                      subheader={`From ${query.from} to ${query.to}`}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Searched on: {timeString}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {analysis?.analysis ? (
                          // Count only valid options (not N/A)
                          `${analysis.analysis.filter((opt: { price: string; day: string }) => 
                            opt.price && 
                            opt.day && 
                            opt.price.toLowerCase() !== 'n/a' && 
                            opt.day.toLowerCase() !== 'n/a'
                          ).length} transport options available`
                        ) : (
                          "No analysis available"
                        )}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => handleViewDetails(analysis)}
                        disabled={!analysis}
                      >
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Dialog for showing detailed analysis */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>
          Cargo Transport Details
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAnalysis ? (
            <CargoAnalysisDisplay analysis={selectedAnalysis} />
          ) : (
            <Typography>No analysis data is available for this cargo.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </AppTheme>
  );
};

export default HistoryPage;
